import { useEffect, useRef, useState } from "react";
import { formatDistanceToNowStrict, differenceInCalendarDays, differenceInHours, differenceInMinutes, differenceInSeconds, type Locale } from 'date-fns';
import { de, fr, nl, pl, sv } from 'date-fns/locale';
import { useCommonTranslation } from "../hooks/useTranslation";
import { formattingLocales } from "../utils/formattingLocale";

interface TimestampProps {
    timestamp?: Date | number | null;
    className?: string;
    short?: boolean;
}

// date-fns ships its own locale objects, so the i18next language has to be
// mapped explicitly; unmapped languages (en, dev) use the date-fns default (English)
const dateFnsLocales: Record<string, Locale> = { de, fr, nl, pl, sv };

const formatTimestamp = (timestamp: Date, t: (key: string, opts?: Record<string, any>) => string, short?: boolean, distanceLocale?: Locale) => {
    const now = new Date();
    const diffInDays = differenceInCalendarDays(now, timestamp);
    const userLocale = formattingLocales();

    if (differenceInSeconds(now, timestamp) < 20) {
        // For < 20 seconds, show "Just now"
        return t('time.just_now');
    } else if (differenceInHours(now, timestamp) < 1) {
        // For < 1 hour, show "X minutes ago" / "X hours ago"
        return formatDistanceToNowStrict(timestamp, { addSuffix: true, locale: distanceLocale });
    } else if (diffInDays < 1) {
        // For < 1 day, show something like "02:15"
        // Use Intl.DateTimeFormat for locale-aware time formatting
        return new Intl.DateTimeFormat(userLocale, {
            timeStyle: 'short',
        }).format(timestamp);
    } else if (diffInDays < 2) {
        // For < 2 days, show something like "Yesterday, HH:mm"
        const timeStr = new Intl.DateTimeFormat(userLocale, {
            hour: '2-digit',
            minute: '2-digit'
        }).format(timestamp);
        return t('time.yesterday_at', { time: timeStr });
    } else if (diffInDays < 7) {
        // For < 7 days, show something like "Monday, HH:mm"
        return new Intl.DateTimeFormat(userLocale, {
            weekday: 'long',
            hour: '2-digit',
            minute: '2-digit'
        }).format(timestamp);
    } else {
        // For >= 7 days, short mode hides the time.
        if (short) {
            return new Intl.DateTimeFormat(userLocale, {
                dateStyle: 'short'
            }).format(timestamp);
        }

        return new Intl.DateTimeFormat(userLocale, {
            dateStyle: 'short',
            timeStyle: 'short'
        }).format(timestamp);
    }
};

const Timestamp: React.FC<TimestampProps> = ({ timestamp, className, short }) => {
    const [strTimestamp, setStrTimestamp] = useState<string | undefined>();
    const timerRef = useRef<any>(null);
    const { t: tCommon, i18n } = useCommonTranslation();
    const activeLanguage = i18n.resolvedLanguage ?? i18n.language ?? 'en';
    const distanceLocale = dateFnsLocales[activeLanguage] ?? dateFnsLocales[activeLanguage.split('-')[0]];

    useEffect(() => {
        if (!timestamp) {
            setStrTimestamp(undefined);
            return;
        }

        // Function to calculate the delay based on the age of the timestamp
        const calculateDelay = (date: Date) => {
            const now = new Date();
            if (differenceInSeconds(now, date) < 60) {
                return 1000; // refresh every second
            } else if (differenceInMinutes(now, date) < 60) {
                return 30000; // refresh every 30 seconds
            } else {
                return 60000; // refresh every minute
            }
        };

        // Function to update the timestamp string and schedule the next refresh
        const refreshTimestamp = () : any => {
            const date = new Date(timestamp);
            setStrTimestamp(formatTimestamp(date, tCommon, short, distanceLocale));

            const delay = calculateDelay(date);
            timerRef.current = setTimeout(refreshTimestamp, delay);
        };

        // Schedule the initial refresh
        refreshTimestamp();

        // Cleanup function to clear the timeout when the component unmounts or when the timestamp changes
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [timestamp, tCommon, short, distanceLocale]);

    return <span className={className}>{strTimestamp}</span>;
};

export default Timestamp;
